import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  useTheme,
  IconButton,
  Button,
  Paper,
  Switch,
  FormControlLabel,
  Divider,
  CircularProgress,
  Chip,
  Stack,
  useMediaQuery,
  Tooltip,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import PlayCircleFilledWhiteRoundedIcon from "@mui/icons-material/PlayCircleFilledWhiteRounded";
import SettingsRoundedIcon from "@mui/icons-material/SettingsRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import { useStateContext } from "contexts/ContextProvider";
import { useAuth } from "contexts/AuthContext";
import api from "services/api";

const ENTITY_CONFIG = [
  {
    key: "Interpretations",
    label: "Interpretations",
    icon: <AutoAwesomeRoundedIcon fontSize="small" />, // decorative only
    rules: [
      {
        key: "interpretation_not_completed_past_date",
        label:
          "Not Completed & Date is past. Task: Complete Interpretation XXX",
      },
      {
        key: "interpretation_completed_not_checked_monthend",
        label: "Completed & Not checked. Task: Check Interpretation XXX",
      },
      {
        key: "interpretation_virtual_missing_address",
        label:
          "If Virtual Assignment && Address is empty. Task: Get Address/Link for Interpretation XXX",
      },
      {
        key: "interpretation_requested_get_interpreter",
        label: "If Requested. Task: Get Interpreter for Interpretation XXX",
      },
      {
        key: "interpretation_mileage_rate_mismatch",
        label:
          "MileageCost / MileageRoundtrip != MileageRate of the same year. Task: Double check the mileage rate for Interpretation XXX",
      },
      {
        key: "interpretation_private_persons_unpaid",
        label:
          "Client is 'Private Persons' AND Status is 'Completed' AND Paid is False. Task: Remind Client 'REQUESTOR NAME' to Pay for Interpretation XXX",
      },
      {
        key: "interpretation_profit_mismatch",
        label:
          "AccountCharge - Total Cost != Profit. Task: Incorrect Profit, double check Service XXX",
      },
      {
        key: "interpretation_margin_mismatch",
        label:
          "Profit / AccountCharge * 100 != Margin. Task: Incorrect Margin, double check Service XXX",
      },
    ],
  },
  {
    key: "Translations",
    label: "Translations",
    icon: <SettingsRoundedIcon fontSize="small" />, // decorative only
    rules: [
      {
        key: "translation_inprogress_seven_days_ago",
        label:
          "In-Progress & Date is 7 days ago. Task: Complete Translation XXX",
      },
      {
        key: "translation_inprogress_deadline_today_tomorrow",
        label:
          "In-Progress & Deadline is tomorrow or today. Task: Complete Translation XXX",
      },
      {
        key: "translation_completed_not_checked_monthend",
        label: "Completed & Not checked. Task: Check Translation XXX",
      },
      {
        key: "translation_mileage_rate_mismatch",
        label:
          "MileageCost / MileageRoundtrip != MileageRate of the same year. Task: Double check the mileage rate for Translation XXX",
      },
      {
        key: "translation_private_persons_unpaid",
        label:
          "Client is 'Private Persons' AND Status is 'Completed' AND Paid is False. Task: Remind Client 'REQUESTOR NAME' to Pay for Translation XXX",
      },
      {
        key: "translation_profit_mismatch",
        label:
          "AccountCharge - Total Cost != Profit. Task: Incorrect Profit, double check Service XXX",
      },
      {
        key: "translation_margin_mismatch",
        label:
          "Profit / AccountCharge * 100 != Margin. Task: Incorrect Margin, double check Service XXX",
      },
    ],
  },
  {
    key: "OtherServices",
    label: "Other Services",
    icon: <SettingsRoundedIcon fontSize="small" />, // decorative only
    rules: [
      {
        key: "otherservice_inprogress_seven_days_ago",
        label:
          "In-Progress & Date is 7 days ago. Task: Complete OtherService XXX",
      },
      {
        key: "otherservice_mileage_rate_mismatch",
        label:
          "MileageCost / MileageRoundtrip != MileageRate of the same year. Task: Double check the mileage rate for OtherService XXX",
      },
      {
        key: "otherservice_private_persons_unpaid",
        label:
          "Client is 'Private Persons' AND Status is 'Completed' AND Paid is False. Task: Remind Client 'REQUESTOR NAME' to Pay for OtherService XXX",
      },
      {
        key: "otherservice_inprogress_deadline_today_tomorrow",
        label:
          "In-Progress & Deadline is tomorrow or today. Task: Complete OtherService XXX",
      },
      {
        key: "otherservice_completed_not_checked_monthend",
        label: "Completed & Not checked. Task: Check OtherService XXX",
      },
      {
        key: "otherservice_profit_mismatch",
        label:
          "AccountCharge - Total Cost != Profit. Task: Incorrect Profit, double check Service XXX",
      },
      {
        key: "otherservice_margin_mismatch",
        label:
          "Profit / AccountCharge * 100 != Margin. Task: Incorrect Margin, double check Service XXX",
      },
    ],
  },
  {
    key: "Clients",
    label: "Clients",
    icon: <SettingsRoundedIcon fontSize="small" />, // decorative only
    rules: [
      {
        key: "client_type_missing",
        label: "Type is null or N/A. Task: Add Type for Client XXX",
      },
      {
        key: "client_contract_missing",
        label:
          "Contract Status is null or N/A or Needs Contract. Task: Get a Contract to sign for Client XXX",
      },
      {
        key: "client_contract_renew_due_next_month",
        label:
          "Contract Status is Current|Sent|Filed AND Renew Date is in the next month. Task: Get an updated Contract to sign for Client XXX",
      },
    ],
  },
  {
    key: "Linguists",
    label: "Linguists",
    icon: <SettingsRoundedIcon fontSize="small" />, // decorative only
    rules: [
      {
        key: "linguist_contract_missing",
        label: "Contract is False. Task: Get a Contract for Linguist XXX",
      },
      {
        key: "linguist_phone_number_bad_format",
        label:
          "Phone Number format is bad. Task: Correct Phone Number for Linguist XXX",
      },
      {
        key: "linguist_badge_missing",
        label: "Badge is False. Task: Get a Badge for Linguist XXX",
      },
      {
        key: "linguist_w9_missing",
        label: "W9 is False. Task: Get a W9 for Linguist XXX",
      },
    ],
  },
  {
    key: "Invoices",
    label: "Invoices",
    icon: <SettingsRoundedIcon fontSize="small" />, // decorative only
    rules: [
      {
        key: "invoice_due_past_unpaid",
        label:
          "Invoice Due Date is today or past AND Not paid. Task: Send Invoice Reminder to Client XXX",
      },
      {
        key: "invoice_service_not_invoiced_last_month",
        label:
          "Service is not invoiced AND service date is last month. Task: Create Invoice for Service XXX",
      },
    ],
  },
];

const buildInitialRuleToggles = () => {
  const toggles = {};
  ENTITY_CONFIG.forEach((entity) => {
    toggles[entity.key] = {};
    entity.rules.forEach((rule) => {
      toggles[entity.key][rule.key] = true;
    });
  });
  return toggles;
};

const TasksAutomated = () => {
  const theme = useTheme();
  const isMobile = !useMediaQuery("(min-width:600px)");
  const { currentUser } = useAuth();
  const {
    setOverlayName,
    triggerPageRefresh,
    showSnackbar,
    openDialog,
    closeDialog,
    handleErrorDialog,
  } = useStateContext();

  const [phase, setPhase] = useState(1);
  const [selectedEntities, setSelectedEntities] = useState(
    ENTITY_CONFIG.map((entity) => entity.key),
  );
  const [ruleToggles, setRuleToggles] = useState(buildInitialRuleToggles);
  const [isSaving, setIsSaving] = useState(false);
  const [runId, setRunId] = useState(null);
  const [progress, setProgress] = useState({
    status: "idle",
    currentStep: 0,
    totalSteps: 0,
    percent: 0,
    label: "",
  });
  const [errorMessage, setErrorMessage] = useState(null);
  const hasTriggeredRefresh = React.useRef(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleClose = () => {
    if (isSaving && progress.status === "running") return;
    setOverlayName("");
  };

  const toggleEntity = (key) => {
    if (isSaving) return;
    setSelectedEntities((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    );
  };

  const handleToggleRule = (entityKey, ruleKey) => {
    if (isSaving) return;
    setRuleToggles((prev) => ({
      ...prev,
      [entityKey]: {
        ...prev[entityKey],
        [ruleKey]: !prev[entityKey][ruleKey],
      },
    }));
  };

  const enabledRulesByEntity = useMemo(() => {
    const result = {};
    selectedEntities.forEach((entityKey) => {
      const enabledRules = Object.entries(ruleToggles[entityKey] || {})
        .filter(([, isOn]) => isOn)
        .map(([ruleKey]) => ruleKey);
      result[entityKey] = enabledRules;
    });
    return result;
  }, [ruleToggles, selectedEntities]);

  const handleNextFromPhase1 = () => {
    setPhase(2);
  };

  const handleBack = () => {
    if (phase === 1) {
      handleClose();
    } else if (phase === 2) {
      setPhase(1);
    } else if (phase === 3) {
      // Allow going back from phase 3 if not currently running
      if (progress.status !== "running") {
        setPhase(2);
        setErrorMessage(null);
        setRunId(null);
        setIsSaving(false);
      }
    }
  };

  const handleDeleteAll = async () => {
    if (isSaving) return;
    openDialog({
      theme,
      title: "Delete Auto-Generated Tasks",
      message:
        "Are you sure you want to delete all auto-generated tasks? This cannot be undone.",
      primaryAction: isDeleting ? "Deleting..." : "Delete All",
      secondaryAction: "Cancel",
      primaryOnClick: async () => {
        if (isDeleting) return;
        setIsDeleting(true);
        try {
          const response = await api.delete("/tasks/automation/all");
          showSnackbar(
            `Deleted ${response.data.deletedCount} auto-generated tasks`,
            theme,
            "success",
          );
          triggerPageRefresh("Auto-generated tasks deleted", theme);
          closeDialog();
        } catch (error) {
          console.error("Error deleting auto-generated tasks:", error);
          handleErrorDialog("Failed to delete auto-generated tasks", theme);
          closeDialog();
        } finally {
          setIsDeleting(false);
        }
      },
      secondaryOnClick: closeDialog,
    });
  };

  const startAutomation = async () => {
    if (!selectedEntities.length) {
      showSnackbar("Select at least one entity type", theme, "error");
      return;
    }

    setIsSaving(true);
    setErrorMessage(null);
    setPhase(3);
    hasTriggeredRefresh.current = false;

    try {
      const payload = {
        entityTypes: selectedEntities,
        rulesByEntity: enabledRulesByEntity,
        initiatedByUserId: currentUser?.id || null,
      };

      const response = await api.post("/tasks/automation/run", payload);
      setRunId(response.data.runId);

      setProgress((prev) => ({
        ...prev,
        status: "running",
        currentStep: 0,
        totalSteps: response.data.totalSteps || prev.totalSteps,
        percent: 0,
        label: "Starting automation...",
      }));
    } catch (error) {
      console.error("Error starting automation:", error);
      setErrorMessage(
        error?.response?.data?.error || "Failed to start automated tasks",
      );
      setIsSaving(false);
      setPhase(2);
    }
  };

  useEffect(() => {
    if (!runId) return undefined;

    const interval = setInterval(async () => {
      try {
        const { data } = await api.get(`/tasks/automation/status/${runId}`);
        setProgress(data);

        if (data.status === "completed") {
          clearInterval(interval);
          setIsSaving(false);
          if (!hasTriggeredRefresh.current) {
            hasTriggeredRefresh.current = true;
            triggerPageRefresh("Automated tasks generated", theme);
          }
        }
        if (data.status === "error") {
          clearInterval(interval);
          setIsSaving(false);
          setErrorMessage(data.errorMessage || "Automation run failed");
        }
      } catch (error) {
        console.error("Error polling automation status:", error);
        setErrorMessage("Failed to fetch automation status");
        clearInterval(interval);
        setIsSaving(false);
      }
    }, 900);

    return () => clearInterval(interval);
  }, [runId, triggerPageRefresh, theme]);

  const renderPhase1 = () => (
    <Box display="flex" flexDirection="column" gap={2} width="100%">
      <Typography
        variant="h4"
        fontWeight="bold"
        color={theme.palette.primary.text}
      >
        Phase 1: Select Entity Types
      </Typography>
      <Typography variant="body2" color={theme.palette.primary.text2}>
        Choose which entity types you want to include for automated task
        generation.
      </Typography>
      <Grid container spacing={2} mt={0.5}>
        {ENTITY_CONFIG.map((entity) => {
          const isSelected = selectedEntities.includes(entity.key);
          return (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={entity.key}>
              <Paper
                onClick={() => toggleEntity(entity.key)}
                sx={{
                  cursor: "pointer",
                  p: 2,
                  height: "100%",
                  borderRadius: "14px",
                  border: `1px solid ${isSelected ? theme.palette.primary.main : theme.palette.background.default}`,
                  backgroundColor: isSelected
                    ? theme.palette.primary.main
                    : theme.palette.background.default,
                  transition: "all 0.2s ease",
                  boxShadow: isSelected ? theme.shadows[4] : theme.shadows[1],
                }}
              >
                <Box display="flex" alignItems="center" gap={1.5}>
                  <Box
                    sx={{
                      width: 20,
                      height: 20,
                      borderRadius: "6px",
                      border: `2px solid ${!isSelected ? theme.palette.primary.main : theme.palette.background.default}`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {isSelected && (
                      <CheckRoundedIcon
                        sx={{
                          fontSize: 16,
                          color: theme.palette.background.default,
                        }}
                      />
                    )}
                  </Box>
                  <Typography
                    variant="body1"
                    fontWeight={600}
                    color={
                      isSelected
                        ? theme.palette.background.default
                        : theme.palette.primary.main
                    }
                  >
                    {entity.label}
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );

  const renderPhase2 = () => {
    const selectedEntityConfigs = ENTITY_CONFIG.filter((entity) =>
      selectedEntities.includes(entity.key),
    );
    const gridSize = selectedEntityConfigs.length === 1 ? 12 : 6;

    return (
      <Box display="flex" flexDirection="column" gap={2} width="100%">
        <Typography
          variant="h4"
          fontWeight="bold"
          color={theme.palette.primary.text}
        >
          Phase 2: Configure Rules
        </Typography>
        <Typography variant="body2" color={theme.palette.primary.text2}>
          Toggle which automation rules to run for each selected entity type.
        </Typography>
        <Grid container spacing={2} mt={0.5}>
          {selectedEntityConfigs.map((entity) => (
            <Grid size={{ xs: 12, md: gridSize }} key={entity.key}>
              <Paper
                sx={{
                  p: 2,
                  borderRadius: "14px",
                  border: `1px solid ${theme.palette.divider}`,
                  backgroundColor: theme.palette.background.default,
                  boxShadow: theme.shadows[1],
                }}
              >
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  mb={1}
                >
                  <Typography
                    variant="h6"
                    fontWeight="bold"
                    color={theme.palette.primary.text}
                  >
                    {entity.label}
                  </Typography>
                  <Chip
                    size="small"
                    label={`${entity.rules.length} rules`}
                    sx={{
                      backgroundColor: theme.palette.background.alt,
                      color: theme.palette.primary.text2,
                      fontWeight: 600,
                    }}
                  />
                </Box>
                <Divider sx={{ mb: 1.5 }} />
                <Stack spacing={1.25}>
                  {entity.rules.map((rule) => (
                    <FormControlLabel
                      key={rule.key}
                      control={
                        <Switch
                          checked={ruleToggles[entity.key]?.[rule.key] ?? false}
                          onChange={() =>
                            handleToggleRule(entity.key, rule.key)
                          }
                          color="primary"
                          disabled={isSaving}
                        />
                      }
                      label={
                        <Typography
                          variant="body2"
                          color={theme.palette.primary.text}
                        >
                          {rule.label}
                        </Typography>
                      }
                      sx={{ alignItems: "flex-start", m: 0 }}
                    />
                  ))}
                </Stack>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  };

  const renderPhase3 = () => {
    const percent = Math.min(Math.max(progress.percent || 0, 0), 100);
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        gap={2}
        width="100%"
        height="100%"
        minHeight="320px"
      >
        <Box position="relative" display="inline-flex">
          <CircularProgress
            variant="determinate"
            value={percent}
            size={140}
            thickness={5}
            color={progress.status === "error" ? "error" : "primary"}
          />
          <Box
            top={0}
            left={0}
            bottom={0}
            right={0}
            position="absolute"
            display="flex"
            alignItems="center"
            justifyContent="center"
            flexDirection="column"
            gap={0.5}
          >
            <Typography
              variant="h4"
              fontWeight="bold"
              color={theme.palette.primary.text}
            >
              {Math.round(percent)}%
            </Typography>
            <Typography variant="caption" color={theme.palette.primary.text2}>
              Step {progress.currentStep || 0} / {progress.totalSteps || 0}
            </Typography>
          </Box>
        </Box>
        <Typography
          variant="h6"
          fontWeight="bold"
          color={theme.palette.primary.text}
        >
          {progress.label || "Working..."}
        </Typography>
        {errorMessage && (
          <Typography color={theme.palette.error.main} textAlign="center">
            {errorMessage}
          </Typography>
        )}
      </Box>
    );
  };

  const renderContent = () => {
    switch (phase) {
      case 1:
        return renderPhase1();
      case 2:
        return renderPhase2();
      case 3:
        return renderPhase3();
      default:
        return null;
    }
  };

  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        bgcolor: "rgba(0, 0, 0, 0.8)",
        zIndex: 1300,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <Box
        onClick={(e) => e.stopPropagation()}
        sx={{
          bgcolor: theme.palette.background.default,
          width: { xs: "100%", sm: "90%", md: "75%", lg: "60%" },
          minWidth: { xs: "100%", sm: "600px" },
          maxWidth: "1300px",
          maxHeight: "85%",
          display: "flex",
          borderRadius: "1.5rem",
          justifyContent: "center",
          alignItems: "flex-start",
          flexDirection: "column",
          color: theme.palette.text.primary,
          p: "0",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
            flexDirection: "row",
            p: isMobile ? "0.5rem 1rem" : "0.5rem 2rem",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "start",
              alignItems: "center",
              flex: 1,
              flexDirection: "row",
              gap: 1,
            }}
          >
            <IconButton color="primary" size="large" onClick={handleBack}>
              <ChevronLeftRoundedIcon fontSize="inherit" />
            </IconButton>

            <Typography variant={isMobile ? "h4" : "h3"} fontWeight="bold">
              Automated Tasks
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            flexGrow: 1,
            p: "1rem 2.3rem 1rem 2.5rem",
            height: "100%",
            width: "100%",
            overflowY: "auto",
            borderRadius: "1.5rem",
            backgroundColor: theme.palette.background.alt,
          }}
        >
          {renderContent()}
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
            p: isMobile
              ? "0.5rem 1rem 0.5rem 1.3rem"
              : "0.5rem 2rem 0.5rem 2.3rem",
            gap: 1,
          }}
        >
          {isMobile ? (
            <Tooltip title="Back">
              <IconButton
                color="primary"
                onClick={handleBack}
                disabled={progress.status === "running"}
                sx={{
                  width: "40px",
                  height: "40px",
                  backgroundColor: theme.palette.background.default,
                  border: `2px solid ${theme.palette.primary.main}`,
                  borderRadius: "12px",
                  "&:hover": {
                    backgroundColor: theme.palette.primary.buttonHover,
                  },
                }}
              >
                <ChevronLeftRoundedIcon />
              </IconButton>
            </Tooltip>
          ) : (
            <Button
              variant="outlined"
              color="primary"
              onClick={handleBack}
              disabled={progress.status === "running"}
              sx={{ borderRadius: "12px" }}
            >
              Back
            </Button>
          )}

          <Box display="flex" gap={1} sx={{ py: "0.25rem" }}>
            {phase < 3 && (
              <>
                {isMobile ? (
                  <Tooltip title="Delete All">
                    <IconButton
                      color="error"
                      onClick={handleDeleteAll}
                      disabled={isSaving || isDeleting}
                      sx={{
                        width: "40px",
                        height: "40px",
                        backgroundColor: theme.palette.background.default,
                        border: `2px solid ${theme.palette.error.main}`,
                        borderRadius: "12px",
                        "&:hover": {
                          backgroundColor: theme.palette.error.main + "20",
                        },
                      }}
                    >
                      <CloseRoundedIcon />
                    </IconButton>
                  </Tooltip>
                ) : (
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={handleDeleteAll}
                    disabled={isSaving || isDeleting}
                    sx={{ borderRadius: "12px" }}
                  >
                    {isDeleting ? "Deleting..." : "Delete All"}
                  </Button>
                )}

                {isMobile ? (
                  <Tooltip title={phase === 1 ? "Next" : "Start"}>
                    <IconButton
                      color="primary"
                      onClick={
                        phase === 1 ? handleNextFromPhase1 : startAutomation
                      }
                      disabled={
                        isSaving ||
                        (phase === 1 && selectedEntities.length === 0)
                      }
                      sx={{
                        width: "40px",
                        height: "40px",
                        backgroundColor: theme.palette.primary.main,
                        borderRadius: "12px",
                        color: "white",
                        "&:hover": {
                          backgroundColor:
                            theme.palette.primary.buttonHoverSelected,
                        },
                        "&:disabled": {
                          backgroundColor: theme.palette.grey[400],
                          color: theme.palette.grey[200],
                        },
                      }}
                    >
                      <PlayCircleFilledWhiteRoundedIcon />
                    </IconButton>
                  </Tooltip>
                ) : (
                  <Button
                    variant="contained"
                    color="primary"
                    endIcon={
                      phase === 2 ? <PlayCircleFilledWhiteRoundedIcon /> : null
                    }
                    onClick={
                      phase === 1 ? handleNextFromPhase1 : startAutomation
                    }
                    disabled={
                      isSaving || (phase === 1 && selectedEntities.length === 0)
                    }
                    sx={{ borderRadius: "12px" }}
                  >
                    {phase === 1 ? "Next" : "Start Automation"}
                  </Button>
                )}
              </>
            )}
            {phase === 3 &&
              (isMobile ? (
                <Tooltip
                  title={progress.status === "completed" ? "Done" : "Close"}
                >
                  <IconButton
                    color="primary"
                    onClick={handleClose}
                    disabled={progress.status === "running"}
                    sx={{
                      width: "40px",
                      height: "40px",
                      backgroundColor: theme.palette.primary.main,
                      borderRadius: "12px",
                      color: "white",
                      "&:hover": {
                        backgroundColor:
                          theme.palette.primary.buttonHoverSelected,
                      },
                      "&:disabled": {
                        backgroundColor: theme.palette.grey[400],
                        color: theme.palette.grey[200],
                      },
                    }}
                  >
                    <CheckRoundedIcon />
                  </IconButton>
                </Tooltip>
              ) : (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleClose}
                  disabled={progress.status === "running"}
                  sx={{ borderRadius: "12px" }}
                >
                  {progress.status === "completed" ? "Done" : "Close"}
                </Button>
              ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default TasksAutomated;
